import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import scrollElementIntoView from 'utils/scroll/scroll';

interface UsePaginationProps<T> {
  items: T[];
  pageSize: number;
  scrollTargetId?: string;
}

const usePagination = <T>({ items, pageSize, scrollTargetId = 'listing-section' }: UsePaginationProps<T>) => {
  const router = useRouter();

  const rawPage = Array.isArray(router.query.page) ? router.query.page[0] : router.query.page;
  const currentPage = Math.max(parseInt(rawPage || '1', 10), 1);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;

    return items.slice(startIndex, startIndex + pageSize);
  }, [items, currentPage, pageSize]);

  const handlePageChange = useCallback(
    (pageNumber: number) => {
      scrollElementIntoView(scrollTargetId);

      const currentParams = new URLSearchParams(window.location.search);
      currentParams.set('page', String(pageNumber));

      router.push(`${router.asPath.split('?')[0]}?${currentParams.toString()}`, undefined, {
        shallow: true,
        scroll: false,
      });
    },
    [router, scrollTargetId],
  );

  return {
    currentPage,
    paginatedItems,
    totalItems: items.length,
    handlePageChange,
  };
};

export default usePagination;
